import { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api, ApiError } from "@/api/client";
import { useMe } from "@/api/hooks";

type Cohort = "stable" | "canary";
// DeviceMode mirrors the Go DeviceRecord.Mode enum (storage.go): "onkit" =
// the model runs on the kit (WS relay, Strategy B); "hostgated" = the model
// runs on a host and the device gates per-response via the ARC serving shim
// (Strategy A). The fleet API always returns a value (defaulting to "onkit"
// for pre-feature records).
type DeviceMode = "onkit" | "hostgated";

interface FleetRow {
  device_id: string;
  cohort: Cohort;
  mode?: DeviceMode;
  enrolled_at?: string;
  last_auth_at?: string;
  description?: string;
  last_telemetry?: unknown;
}

interface FleetResponse {
  items: FleetRow[];
  fleet_size: number;
}

interface DeviceCredential {
  status: "enrolled" | "rotated";
  device_id: string;
  token: string;
  cohort: Cohort;
  mode?: DeviceMode;
  description?: string;
}

export function FleetPage() {
  const me = useMe();
  const qc = useQueryClient();
  const isAdmin = me.data?.role === "admin";

  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [credential, setCredential] = useState<DeviceCredential | null>(null);

  const q = useQuery<FleetResponse>({
    queryKey: ["fleet"],
    queryFn: () => api<FleetResponse>("/api/v1/fleet"),
    refetchInterval: 30_000,
  });

  const invalidate = () => qc.invalidateQueries({ queryKey: ["fleet"] });

  const onMutationError = (e: unknown) => {
    if (e instanceof ApiError) {
      const body = (typeof e.body === "object" && e.body) ? (e.body as { error?: string }) : null;
      setError(body?.error ?? e.message);
    } else {
      setError(String(e));
    }
  };

  const enroll = useMutation({
    mutationFn: (input: { device_id: string; cohort: Cohort; mode: DeviceMode; description: string }) =>
      api<DeviceCredential>("/api/v1/admin/devices", { method: "POST", body: input }),
    onSuccess: (d) => {
      setCredential(d);
      setNotice(`Enrolled '${d.device_id}'. Copy the token now — it cannot be retrieved later.`);
      setError(null);
      invalidate();
    },
    onError: onMutationError,
  });

  const rotate = useMutation({
    mutationFn: (device_id: string) =>
      api<DeviceCredential>(
        `/api/v1/admin/devices/${encodeURIComponent(device_id)}/rotate`,
        { method: "POST" },
      ),
    onSuccess: (d) => {
      setCredential(d);
      setNotice(`Token rotated for '${d.device_id}'. Old token is invalid.`);
      setError(null);
      invalidate();
    },
    onError: onMutationError,
  });

  const remove = useMutation({
    mutationFn: (device_id: string) =>
      api(`/api/v1/admin/devices/${encodeURIComponent(device_id)}`, { method: "DELETE" }),
    onSuccess: (_d, device_id) => {
      setNotice(`Removed device '${device_id}'.`);
      setError(null);
      invalidate();
    },
    onError: onMutationError,
  });

  if (q.isLoading) return <p className="text-slate-500 dark:text-slate-400">Loading…</p>;
  if (q.error) {
    if (q.error instanceof ApiError && q.error.status === 401) {
      return (
        <div className="rounded bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700 px-3 py-2 text-sm">
          Sign in as an operator to view the fleet.
        </div>
      );
    }
    return (
      <div className="rounded bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-700 px-3 py-2 text-sm text-red-700 dark:text-red-300">
        Could not load fleet: {(q.error as Error).message}
      </div>
    );
  }
  const data = q.data!;

  return (
    <div>
      <header className="mb-4 flex items-start justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold">Fleet</h1>
          <p className="text-sm text-slate-600 dark:text-slate-400 mt-1">
            Enrolled devices with the most-recent telemetry the SA has on file.
            Auto-refreshes every 30 s.
          </p>
        </div>
        {!isAdmin && (
          <span className="text-xs text-slate-500 dark:text-slate-400 italic">
            Administrators can enroll, rotate, or remove devices.
          </span>
        )}
      </header>

      {error && (
        <div className="rounded bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-700 px-3 py-2 text-sm text-red-700 dark:text-red-300 mb-3 flex items-start justify-between gap-3">
          <span>{error}</span>
          <button onClick={() => setError(null)} className="text-xs text-red-600 dark:text-red-400 hover:text-red-900 dark:hover:text-red-200">dismiss</button>
        </div>
      )}
      {notice && !credential && (
        <div className="rounded bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-700 px-3 py-2 text-sm text-green-700 dark:text-green-300 mb-3 flex items-start justify-between gap-3">
          <span>{notice}</span>
          <button onClick={() => setNotice(null)} className="text-xs text-green-700 dark:text-green-300 hover:text-green-900 dark:hover:text-green-200">dismiss</button>
        </div>
      )}

      {credential && (
        <CredentialReveal
          credential={credential}
          onClose={() => { setCredential(null); setNotice(null); }}
        />
      )}

      {isAdmin && (
        <EnrollForm
          disabled={enroll.isPending}
          onSubmit={(device_id, cohort, mode, description) =>
            enroll.mutate({ device_id, cohort, mode, description })
          }
        />
      )}

      <div className="rounded border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 dark:bg-slate-700 text-left text-xs uppercase tracking-wide text-slate-600 dark:text-slate-400">
            <tr>
              <th className="px-3 py-2">Device ID</th>
              <th className="px-3 py-2">Cohort</th>
              <th className="px-3 py-2">Mode</th>
              <th className="px-3 py-2">Enrolled</th>
              <th className="px-3 py-2">Last auth</th>
              <th className="px-3 py-2">Telemetry</th>
              <th className="px-3 py-2">CLI</th>
              <th className="px-3 py-2">Description</th>
              {isAdmin && <th className="px-3 py-2">Actions</th>}
            </tr>
          </thead>
          <tbody>
            {data.items.length === 0 && (
              <tr><td colSpan={isAdmin ? 9 : 8} className="px-3 py-6 text-center text-slate-500 dark:text-slate-400">
                No devices enrolled yet.{isAdmin && " Use the form above to add one."}
              </td></tr>
            )}
            {data.items.map((d) => (
              <tr key={d.device_id} className="border-t border-slate-100 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-700 align-top">
                <td className="px-3 py-2 font-mono">{d.device_id}</td>
                <td className="px-3 py-2">
                  <span className={`rounded px-2 py-0.5 text-xs font-semibold uppercase ${
                    d.cohort === "canary" ? "bg-amber-50 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300" : "bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-300"
                  }`}>{d.cohort}</span>
                </td>
                <td className="px-3 py-2"><ModeBadge mode={d.mode} /></td>
                <td className="px-3 py-2 font-mono text-xs">{d.enrolled_at ?? "—"}</td>
                <td className="px-3 py-2 font-mono text-xs">{d.last_auth_at ?? "—"}</td>
                <td className="px-3 py-2 text-xs text-slate-500 dark:text-slate-400">{d.last_telemetry ? "✓" : "—"}</td>
                <td className="px-3 py-2 whitespace-nowrap">
                  <CliCell online={!!d.last_telemetry} />
                  <Link
                    to={`/fleet/${encodeURIComponent(d.device_id)}`}
                    className="ml-2 text-xs text-blue-700 dark:text-blue-400 hover:underline"
                  >
                    Open CLI →
                  </Link>
                </td>
                <td className="px-3 py-2 text-slate-700 dark:text-slate-300">{d.description ?? ""}</td>
                {isAdmin && (
                  <td className="px-3 py-2 space-x-1 whitespace-nowrap">
                    <button
                      disabled={rotate.isPending}
                      className="text-xs rounded border border-slate-300 dark:border-slate-600 px-2 py-0.5 hover:bg-slate-100 dark:hover:bg-slate-700 disabled:opacity-30"
                      onClick={() => {
                        if (confirm(`Rotate token for ${d.device_id}? The old token will stop working immediately.`)) {
                          rotate.mutate(d.device_id);
                        }
                      }}
                    >rotate</button>
                    <button
                      disabled={remove.isPending}
                      className="text-xs rounded border border-red-300 dark:border-red-700 text-red-700 dark:text-red-300 px-2 py-0.5 hover:bg-red-50 dark:hover:bg-red-900/20 disabled:opacity-30"
                      onClick={() => {
                        if (confirm(`Remove device ${d.device_id}? It will no longer be able to authenticate.`)) {
                          remove.mutate(d.device_id);
                        }
                      }}
                    >remove</button>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="text-xs text-slate-500 dark:text-slate-400 mt-2">{data.fleet_size} device(s) total.</p>
    </div>
  );
}

// --- subcomponents -----------------------------------------------------------

// ModeBadge renders a device's inference mode. on-kit (default) = the model runs
// on the kit (WS relay); host-gated = the model runs on a host and the device
// gates per-response via the ARC serving shim. Defaults to on-kit when the field
// is absent (pre-feature records).
function ModeBadge({ mode }: { mode?: DeviceMode }) {
  const m: DeviceMode = mode ?? "onkit";
  const hostgated = m === "hostgated";
  return (
    <span
      className={
        "inline-block rounded px-2 py-0.5 text-xs font-semibold " +
        (hostgated
          ? "bg-indigo-50 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300"
          : "bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-300")
      }
      title={
        hostgated
          ? "Host-gated: the model runs on a host; this device guards the response (per-response silicon gate + attestation receipt) via the ARC serving shim."
          : "On-kit: the model runs on the device (on-die generation, relayed over the fleet agent channel)."
      }
    >
      {hostgated ? "host-gated" : "on-kit"}
    </span>
  );
}

// CliCell shows a best-effort online/CLI-ready hint. The fleet API does not
// expose channel-liveness or a pinned-key flag (see api_admin.go
// handleAPIFleet), so "online" here is a PROXY (recent telemetry on file), not
// a confirmed live remote-CLI channel. The detail page's connect attempt is the
// authoritative source of truth — see the Go-gap note in the build report.
function CliCell({ online }: { online: boolean }) {
  return (
    <span
      className={
        "inline-flex items-center gap-1 rounded px-2 py-0.5 text-xs font-semibold " +
        (online
          ? "bg-green-50 dark:bg-green-900/30 text-green-700 dark:text-green-300"
          : "bg-slate-100 dark:bg-slate-700 text-slate-500 dark:text-slate-400")
      }
      title={
        online
          ? "Device has recent telemetry on file (best-effort hint; the CLI tab confirms the live channel)."
          : "No recent telemetry; the device may be offline. Open the CLI to check the live channel."
      }
    >
      <span
        className={
          "h-1.5 w-1.5 rounded-full " + (online ? "bg-green-500" : "bg-slate-400")
        }
        aria-hidden="true"
      />
      {online ? "online" : "offline?"}
    </span>
  );
}

function EnrollForm({
  disabled, onSubmit,
}: {
  disabled: boolean;
  onSubmit: (device_id: string, cohort: Cohort, mode: DeviceMode, description: string) => void;
}) {
  const [deviceID, setDeviceID] = useState("");
  const [cohort, setCohort] = useState<Cohort>("stable");
  const [mode, setMode] = useState<DeviceMode>("onkit");
  const [description, setDescription] = useState("");
  return (
    <form
      className="rounded border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 px-3 py-3 mb-4 grid grid-cols-1 md:grid-cols-4 gap-3 text-sm items-end"
      onSubmit={(e) => {
        e.preventDefault();
        if (!deviceID.trim()) return;
        onSubmit(deviceID.trim(), cohort, mode, description.trim());
        setDeviceID("");
        setDescription("");
        setCohort("stable");
        setMode("onkit");
      }}
    >
      <label className="flex flex-col">
        <span className="text-xs text-slate-500 dark:text-slate-400">Device ID</span>
        <input
          type="text"
          value={deviceID}
          onChange={(e) => setDeviceID(e.target.value)}
          required
          autoComplete="off"
          placeholder="e.g. kit-icicle-042"
          className="font-mono mt-1 rounded border border-slate-300 dark:border-slate-600 px-2 py-1"
        />
      </label>
      <label className="flex flex-col">
        <span className="text-xs text-slate-500 dark:text-slate-400">Cohort</span>
        <select
          value={cohort}
          onChange={(e) => setCohort(e.target.value as Cohort)}
          className="mt-1 rounded border border-slate-300 dark:border-slate-600 px-2 py-1 bg-white dark:bg-slate-800"
        >
          <option value="stable">stable</option>
          <option value="canary">canary</option>
        </select>
      </label>
      <label className="flex flex-col">
        <span className="text-xs text-slate-500 dark:text-slate-400">Inference mode</span>
        <select
          value={mode}
          onChange={(e) => setMode(e.target.value as DeviceMode)}
          className="mt-1 rounded border border-slate-300 dark:border-slate-600 px-2 py-1 bg-white dark:bg-slate-800"
          title={
            mode === "hostgated"
              ? "Host-gated: the model runs on a host; this device guards the response via the ARC serving shim."
              : "On-kit: the model runs on the device (on-die generation)."
          }
        >
          <option value="onkit">On-kit — model runs on the device</option>
          <option value="hostgated">Host-gated — device guards a host model</option>
        </select>
      </label>
      <label className="flex flex-col md:col-span-1">
        <span className="text-xs text-slate-500 dark:text-slate-400">Description (optional)</span>
        <input
          type="text"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          autoComplete="off"
          placeholder="ESS-EU lab, rack 3"
          className="mt-1 rounded border border-slate-300 dark:border-slate-600 px-2 py-1"
        />
      </label>
      <button
        type="submit"
        disabled={disabled}
        className="rounded bg-slate-900 dark:bg-purple-600 text-white py-1.5 font-medium hover:bg-slate-800 dark:hover:bg-purple-500 disabled:opacity-50 md:col-span-4"
      >
        {disabled ? "Enrolling…" : "Enroll device"}
      </button>
    </form>
  );
}

function CredentialReveal({
  credential, onClose,
}: {
  credential: DeviceCredential;
  onClose: () => void;
}) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(credential.token);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // Clipboard API can be blocked on http:// — fall back to a select-all
      // hint by no-oping; the input is already select-all on focus.
    }
  };
  return (
    <div className="rounded border-2 border-amber-300 dark:border-amber-600 bg-amber-50 px-4 py-3 mb-4">
      <div className="flex items-start justify-between gap-3 mb-2">
        <div>
          <p className="text-sm font-semibold text-amber-900 dark:text-amber-200">
            {credential.status === "rotated" ? "Token rotated" : "Device enrolled"} —{" "}
            <span className="font-mono">{credential.device_id}</span>
          </p>
          <p className="text-xs text-amber-800 dark:text-amber-300 mt-1">
            Copy the token below now. It is shown <strong>once</strong> and cannot be
            retrieved later — if you lose it, rotate the device to mint a fresh one.
          </p>
        </div>
        <button onClick={onClose} className="text-xs text-amber-900 dark:text-amber-200 hover:underline">dismiss</button>
      </div>
      <div className="flex gap-2">
        <input
          type="text"
          readOnly
          value={credential.token}
          onFocus={(e) => e.currentTarget.select()}
          className="flex-1 font-mono text-xs bg-white dark:bg-slate-800 border border-amber-200 dark:border-amber-700 rounded px-2 py-1"
        />
        <button
          onClick={copy}
          className="rounded bg-amber-700 text-white text-xs px-3 py-1 font-medium hover:bg-amber-800"
        >
          {copied ? "Copied!" : "Copy"}
        </button>
      </div>
    </div>
  );
}
